import React, { useState, useEffect } from 'react';
import { 
    Star, 
    Send, 
    X, 
    Heart,
    ThumbsUp,
    ThumbsDown,
    Sparkles,
    MessageSquare,
    Gift,
    Check
} from 'lucide-react';
import { PrimaryButton, SecondaryButton } from './brand/BrandButton';
import { Heading, BodyText, SmallText } from './brand/BrandTypography';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tools list for selection
const TOOLS_LIST = [
    { id: 'ai-guide', name: 'AI Guide' },
    { id: 'breathing', name: 'Breathing Patterns' },
    { id: 'emdr', name: 'EMDR/Bilateral Regulation' },
    { id: 'micro-reset', name: 'Micro Reset' },
    { id: 'body-scan', name: 'Body Scan' },
    { id: 'calm-cycle', name: 'Calm Cycle' },
    { id: 'meditations', name: 'Guided Meditations' },
    { id: 'relationship-hub', name: 'Relationship Hub' },
    { id: 'check-in', name: 'Check-ins' },
    { id: 'still-sphere', name: 'Still Sphere' }
];

/**
 * ExitSurveyModal - End-of-trial feedback questionnaire
 * Shows when promo period is expiring/expired
 */
export const ExitSurveyModal = ({ isOpen, onClose, onComplete, daysRemaining }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    
    // Survey responses
    const [overallRating, setOverallRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [helpfulTools, setHelpfulTools] = useState([]);
    const [wouldRecommend, setWouldRecommend] = useState(null);
    const [likelyToSubscribe, setLikelyToSubscribe] = useState(0);
    const [favoriteFeature, setFavoriteFeature] = useState('');
    const [whatWouldImprove, setWhatWouldImprove] = useState('');
    const [missingFeatures, setMissingFeatures] = useState('');
    const [otherFeedback, setOtherFeedback] = useState('');

    const totalSteps = 4;

    const toggleTool = (toolId) => {
        setHelpfulTools(prev => 
            prev.includes(toolId) 
                ? prev.filter(t => t !== toolId)
                : [...prev, toolId]
        );
    };

    const handleSubmit = async () => {
        setLoading(true);

        try {
            const token = localStorage.getItem('co_regulate_token');
            const response = await fetch(`${API_URL}/api/feedback/exit-survey`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    overall_rating: overallRating,
                    most_helpful_tools: helpfulTools,
                    would_recommend: wouldRecommend,
                    likely_to_subscribe: likelyToSubscribe,
                    favorite_feature: favoriteFeature || null,
                    what_would_improve: whatWouldImprove || null,
                    missing_features: missingFeatures || null,
                    other_feedback: otherFeedback || null
                })
            });

            const data = await response.json();

            if (data.success) {
                toast.success('Thank you for your feedback!');
                if (onComplete) onComplete();
                onClose();
            } else {
                toast.error('Failed to submit survey');
            }
        } catch (error) {
            console.error('Error submitting exit survey:', error);
            toast.error('Failed to submit survey. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const canProceed = () => {
        switch (step) {
            case 1: return overallRating > 0;
            case 2: return helpfulTools.length > 0;
            case 3: return wouldRecommend !== null && likelyToSubscribe > 0;
            case 4: return true; // Optional fields
            default: return false;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
            <div 
                className="glass-card rounded-2xl p-6 max-w-lg w-full shadow-2xl animate-in fade-in zoom-in duration-200 my-8"
                data-testid="exit-survey-modal"
            >
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FFBB98] to-[#E09060] flex items-center justify-center">
                            <Gift className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <Heading level={4} className="text-[#1D6B6B]">
                                {daysRemaining > 0 
                                    ? `Your trial ends in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`
                                    : 'Your trial has ended'
                                }
                            </Heading>
                            <SmallText className="text-[#7D8E95]">
                                Help us improve with a quick survey
                            </SmallText>
                        </div>
                    </div>
                    <button 
                        onClick={onClose}
                        className="p-1 rounded-full hover:bg-[#7D8E95]/10 transition-colors"
                    >
                        <X className="w-5 h-5 text-[#7D8E95]" />
                    </button>
                </div>

                {/* Progress Bar */}
                <div className="flex gap-1 mb-6">
                    {[1, 2, 3, 4].map((s) => (
                        <div 
                            key={s}
                            className={cn(
                                'h-1 flex-1 rounded-full transition-colors',
                                s <= step ? 'bg-[#1D6B6B]' : 'bg-[#7D8E95]/20'
                            )}
                        />
                    ))}
                </div>

                {/* Step 1: Overall Rating */}
                {step === 1 && (
                    <div className="space-y-6">
                        <div className="text-center">
                            <Heading level={3} className="text-[#1D6B6B] mb-2">
                                How would you rate your overall experience?
                            </Heading>
                            <SmallText className="text-[#7D8E95]">
                                Your honest feedback helps us improve
                            </SmallText>
                        </div>

                        <div className="flex justify-center gap-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    onClick={() => setOverallRating(star)}
                                    onMouseEnter={() => setHoverRating(star)}
                                    onMouseLeave={() => setHoverRating(0)}
                                    className="p-1 transition-transform hover:scale-110 active:scale-95"
                                >
                                    <Star 
                                        className={cn(
                                            'w-12 h-12 transition-colors',
                                            (hoverRating || overallRating) >= star
                                                ? 'fill-[#FFBB98] text-[#FFBB98]'
                                                : 'fill-transparent text-[#7D8E95]/30'
                                        )}
                                    />
                                </button>
                            ))}
                        </div>

                        {overallRating > 0 && (
                            <div className="text-center">
                                <BodyText className="text-[#1D6B6B] font-medium">
                                    {overallRating === 1 && "We're sorry to hear that"}
                                    {overallRating === 2 && "Thanks for your honesty"}
                                    {overallRating === 3 && "Room to grow"}
                                    {overallRating === 4 && "Great to hear!"}
                                    {overallRating === 5 && "Amazing! Thank you!"}
                                </BodyText>
                            </div>
                        )}
                    </div>
                )}

                {/* Step 2: Most Helpful Tools */}
                {step === 2 && (
                    <div className="space-y-4">
                        <div className="text-center mb-4">
                            <Heading level={3} className="text-[#1D6B6B] mb-2">
                                Which tools helped you the most?
                            </Heading>
                            <SmallText className="text-[#7D8E95]">
                                Select all that apply
                            </SmallText>
                        </div>

                        <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                            {TOOLS_LIST.map((tool) => (
                                <button
                                    key={tool.id}
                                    onClick={() => toggleTool(tool.id)}
                                    className={cn(
                                        'p-3 rounded-xl text-left transition-all',
                                        helpfulTools.includes(tool.id)
                                            ? 'bg-[#1D6B6B] text-white'
                                            : 'bg-[#7D8E95]/10 text-[#344648] hover:bg-[#7D8E95]/20'
                                    )}
                                >
                                    <div className="flex items-center gap-2">
                                        {helpfulTools.includes(tool.id) && (
                                            <Check className="w-4 h-4 flex-shrink-0" />
                                        )}
                                        <SmallText className={helpfulTools.includes(tool.id) ? 'text-white' : ''}>
                                            {tool.name}
                                        </SmallText>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Step 3: Recommend & Subscribe */}
                {step === 3 && (
                    <div className="space-y-6">
                        {/* Would Recommend */}
                        <div>
                            <Heading level={4} className="text-[#1D6B6B] mb-3 text-center">
                                Would you recommend CoRegulate to others with ADHD?
                            </Heading>
                            <div className="flex justify-center gap-4">
                                <button
                                    onClick={() => setWouldRecommend(true)}
                                    className={cn(
                                        'flex items-center gap-2 px-6 py-3 rounded-xl transition-all',
                                        wouldRecommend === true
                                            ? 'bg-green-100 border-2 border-green-500 text-green-700'
                                            : 'bg-[#7D8E95]/10 text-[#344648] hover:bg-[#7D8E95]/20'
                                    )}
                                >
                                    <ThumbsUp className="w-5 h-5" />
                                    <span>Yes</span>
                                </button>
                                <button
                                    onClick={() => setWouldRecommend(false)}
                                    className={cn(
                                        'flex items-center gap-2 px-6 py-3 rounded-xl transition-all',
                                        wouldRecommend === false
                                            ? 'bg-red-100 border-2 border-red-400 text-red-700'
                                            : 'bg-[#7D8E95]/10 text-[#344648] hover:bg-[#7D8E95]/20'
                                    )}
                                >
                                    <ThumbsDown className="w-5 h-5" />
                                    <span>Not yet</span>
                                </button>
                            </div>
                        </div>

                        {/* Likely to Subscribe */}
                        <div>
                            <Heading level={4} className="text-[#1D6B6B] mb-3 text-center">
                                How likely are you to subscribe?
                            </Heading>
                            <div className="flex justify-center gap-2">
                                {[1, 2, 3, 4, 5].map((num) => (
                                    <button
                                        key={num}
                                        onClick={() => setLikelyToSubscribe(num)}
                                        className={cn(
                                            'w-12 h-12 rounded-xl font-medium transition-all',
                                            likelyToSubscribe === num
                                                ? 'bg-[#1D6B6B] text-white scale-110'
                                                : 'bg-[#7D8E95]/10 text-[#344648] hover:bg-[#7D8E95]/20'
                                        )}
                                    >
                                        {num}
                                    </button>
                                ))}
                            </div>
                            <div className="flex justify-between mt-2 px-2">
                                <SmallText className="text-[#7D8E95]">Not likely</SmallText>
                                <SmallText className="text-[#7D8E95]">Very likely</SmallText>
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 4: Open Feedback */}
                {step === 4 && (
                    <div className="space-y-4">
                        <div className="text-center mb-2">
                            <Heading level={3} className="text-[#1D6B6B] mb-2">
                                Final thoughts (optional)
                            </Heading>
                            <SmallText className="text-[#7D8E95]">
                                Your insights help us build better tools
                            </SmallText>
                        </div>

                        <div>
                            <label className="block mb-1">
                                <SmallText className="text-[#7D8E95]">What's your favorite feature?</SmallText>
                            </label>
                            <input
                                type="text"
                                value={favoriteFeature}
                                onChange={(e) => setFavoriteFeature(e.target.value)}
                                placeholder="e.g., The AI Guide really understood me"
                                className="w-full px-4 py-2 rounded-xl bg-white/50 border border-[#7D8E95]/20 text-[#1D6B6B] placeholder-[#7D8E95] focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/50"
                            />
                        </div>

                        <div>
                            <label className="block mb-1">
                                <SmallText className="text-[#7D8E95]">What would make it better?</SmallText>
                            </label>
                            <input
                                type="text"
                                value={whatWouldImprove}
                                onChange={(e) => setWhatWouldImprove(e.target.value)}
                                placeholder="e.g., More meditation options"
                                className="w-full px-4 py-2 rounded-xl bg-white/50 border border-[#7D8E95]/20 text-[#1D6B6B] placeholder-[#7D8E95] focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/50"
                            />
                        </div>

                        <div>
                            <label className="block mb-1">
                                <SmallText className="text-[#7D8E95]">Any features you wish we had?</SmallText>
                            </label>
                            <input
                                type="text"
                                value={missingFeatures}
                                onChange={(e) => setMissingFeatures(e.target.value)}
                                placeholder="e.g., Integration with calendar"
                                className="w-full px-4 py-2 rounded-xl bg-white/50 border border-[#7D8E95]/20 text-[#1D6B6B] placeholder-[#7D8E95] focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/50"
                            />
                        </div>

                        <div>
                            <label className="block mb-1">
                                <SmallText className="text-[#7D8E95]">Anything else you'd like to share?</SmallText>
                            </label>
                            <textarea
                                value={otherFeedback}
                                onChange={(e) => setOtherFeedback(e.target.value)}
                                placeholder="Your thoughts matter to us..."
                                rows={2}
                                className="w-full px-4 py-2 rounded-xl bg-white/50 border border-[#7D8E95]/20 text-[#1D6B6B] placeholder-[#7D8E95] focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/50 resize-none"
                            />
                        </div>
                    </div>
                )}

                {/* Navigation */}
                <div className="flex gap-3 mt-6">
                    {step > 1 && (
                        <SecondaryButton 
                            onClick={() => setStep(step - 1)}
                            className="flex-1"
                        >
                            Back
                        </SecondaryButton>
                    )}
                    
                    {step < totalSteps ? (
                        <PrimaryButton 
                            onClick={() => setStep(step + 1)}
                            disabled={!canProceed()}
                            className="flex-1"
                        >
                            Continue
                        </PrimaryButton>
                    ) : (
                        <PrimaryButton 
                            onClick={handleSubmit}
                            disabled={loading}
                            className="flex-1"
                        >
                            {loading ? 'Submitting...' : (
                                <span className="flex items-center justify-center gap-2">
                                    <Send className="w-4 h-4" />
                                    Submit Feedback
                                </span>
                            )}
                        </PrimaryButton>
                    )}
                </div>

                {/* Skip option on last step */}
                {step === totalSteps && (
                    <button 
                        onClick={handleSubmit}
                        className="w-full text-center text-sm text-[#7D8E95] hover:text-[#1D6B6B] transition-colors mt-3"
                    >
                        Skip optional questions
                    </button>
                )}
            </div>
        </div>
    );
};

/**
 * Hook to manage exit survey display
 */
export const useExitSurvey = () => {
    const [showSurvey, setShowSurvey] = useState(false);
    const [daysRemaining, setDaysRemaining] = useState(0);

    useEffect(() => {
        const checkSurveyStatus = async () => {
            try {
                const token = localStorage.getItem('co_regulate_token');
                if (!token) return;

                const response = await fetch(`${API_URL}/api/feedback/exit-survey/status`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                
                if (response.ok) {
                    const data = await response.json();
                    if (data.show_survey) {
                        setDaysRemaining(data.days_until_expiry);
                        setShowSurvey(true);
                    }
                }
            } catch (error) {
                console.error('Error checking exit survey status:', error);
            }
        };

        // Check on mount and periodically
        checkSurveyStatus();
        
        // Also check when user returns to app
        const handleFocus = () => checkSurveyStatus();
        window.addEventListener('focus', handleFocus);
        
        return () => window.removeEventListener('focus', handleFocus);
    }, []);

    return {
        showSurvey,
        daysRemaining,
        closeSurvey: () => setShowSurvey(false)
    };
};

export default ExitSurveyModal;
